import { Component, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { Subject, Subscription } from 'rxjs';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { PayoutDAO } from '../../../../../core/services/dao/payoutDAO';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { VesselRate } from 'anchor-shared/lib/model/entity/vessel-rate';
import { VesselRateDAO } from '../../../../../core/services/dao/vesselRateDAO';
import { debounceTime } from 'rxjs/operators';
import { AddOnDAO } from '../../../../../core/services/dao/addOnDAO';
import {
  getAddOnsTotal,
  getDurationTypeForCalcService,
} from 'anchor-shared/lib/service/pure-functions/charter-pricing/charter-pricing-helper';
import { CustomerAddOn } from 'anchor-shared/lib/model/entity/addOn';
import { VesselRateCalculationService } from '../../../../../core/services/vessel-rate-calculation-service';

@Component({
  selector: 'app-custom-offer-payouts-dialog',
  templateUrl: './custom-offer-payouts-dialog.component.html',
  styleUrls: ['./custom-offer-payouts-dialog.component.scss'],
})
export class CustomOfferPayoutsDialogComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  // provided data
  charter: Charter;
  offerTitle: string;
  offerPrice: number;
  offerDuration: number;
  offerVesselId: number;
  // managed data
  customRateAmounts;
  customOriginalRates;
  customAddOns: CustomerAddOn[] = [];
  customPayouts: Payout[];
  customPayoutAmount: number;
  bookingRateAmount: number = 0;
  ownerPay: number;
  ownerTotalPayout: number;
  // utility
  viewOnly: boolean;
  errors: { [id: number]: string; form: string } = { form: '' };
  updatingPayouts: boolean = false;
  subs: Subscription[] = [];
  inputSubject = new Subject();
  debounceTimeMs = 800;
  RateType = RateType;

  constructor(
    private payoutDAO: PayoutDAO,
    private vesselRateDAO: VesselRateDAO,
    private addOnDAO: AddOnDAO,
    private calcService: VesselRateCalculationService,
    private dialogRef: MatDialogRef<CustomOfferPayoutsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
  ) {
    this.charter = this.data.charter;
    this.offerTitle = this.data.offerTitle;
    this.offerPrice = this.data.offerPrice;
    this.offerDuration = this.data.offerDuration;
    this.offerVesselId = this.data.offerVesselId;
    this.customOriginalRates = this.data.customOriginalRates;
    this.customAddOns = this.data.customAddOns;
    this.viewOnly = this.data.viewOnly;

    if (!this.data.customOriginalRates) {
      this.vesselRateDAO
        .getByVesselId(this.offerVesselId)
        .subscribe((rates) => {
          this.customOriginalRates = rates;
          this.buildCustomRates(this.customOriginalRates);
          this.calculateTotalPayout();
        });
    } else {
      this.buildCustomRates(this.customOriginalRates);
      this.calculateTotalPayout();
    }
  }

  ngOnInit() {
    const inputSub = this.inputSubject
      .pipe(debounceTime(this.debounceTimeMs))
      .subscribe(() => {
        this.calculateTotalPayout();
      });
    this.subs.push(inputSub);
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  // utility
  onInput() {
    this.updatingPayouts = true;
    // control debounce
    this.inputSubject.next();
  }

  trackByFn(index, item) {
    return index;
  }

  buildCustomRates(originalRates) {
    const customRateAmounts = {};
    originalRates.forEach((rate) => {
      if ([RateType.Owner, RateType.BookingFee].includes(rate.rateType)) return;
      customRateAmounts[rate.id] = this.calculateRatePay(rate);
    });

    this.customRateAmounts = customRateAmounts;
  }

  calculateRatePay(rate: VesselRate): number {
    const ratePay = this.calcService.calculateVesselRate(
      rate,
      getDurationTypeForCalcService(this.offerDuration),
      this.offerDuration,
      false,
    );

    return ratePay;
  }

  getDisplayableOriginalRates() {
    const displayOrder = [
      RateType.Captain,
      RateType.Crew,
      RateType.FuelIncluded,
      RateType.Cleaning,
    ];
    const rates = this.customOriginalRates.filter(
      (x) => ![RateType.Owner, RateType.BookingFee].includes(x.rateType),
    );

    return rates.sort((a, b) => {
      return (
        displayOrder.indexOf(a.rateType) - displayOrder.indexOf(b.rateType)
      );
    });
  }

  sumCustomRateAmountsWithMarkup(): number {
    let markedUpTotal = 0;
    this.customOriginalRates.forEach((rate) => {
      if ([RateType.Owner, RateType.BookingFee].includes(rate.rateType)) return;

      markedUpTotal +=
        this.customRateAmounts[rate.id] * (1 + rate.markupPercentage);
    });
    markedUpTotal += getAddOnsTotal(this.customAddOns || [], 'required', true);

    return markedUpTotal;
  }

  validatePayouts() {
    if (this.ownerPay < 0) {
      this.errors.form = 'Owner pay must be greater than zero.';
    } else {
      this.errors.form = '';
    }

    this.customOriginalRates.forEach((rate) => {
      let error = '';
      if (rate.rateType === RateType.Cleaning && Number(rate.customRate < 0)) {
        error = 'Cleaning fee must be greater than or equal to zero.';
      }
      if (rate.rateType !== RateType.Cleaning && rate.hourlyRate < 0) {
        error = 'Value must be greater than or equal to zero.';
      }
      this.errors[rate.id] = error;
    });
  }

  recalculateAddOns() {
    this.customAddOns = this.customAddOns.map((addOn: any) => {
      addOn.price = addOn.payoutPrice / addOn.quantity;
      return { ...addOn };
    });
  }

  // data
  calculateTotalPayout() {
    if (this.customAddOns?.length > 0) {
      this.recalculateAddOns();
    }
    this.customOriginalRates = this.rebuildOriginalRates();
    let ownerPayout = 0;
    ownerPayout += this.customOriginalRates.reduce((acc, curr) => {
      if (curr.ratePayee === 'Boat Owner') {
        if (curr.rateType === RateType.Cleaning) {
          return acc + Number(curr.customRate);
        }
        return acc + curr.hourlyRate * this.offerDuration;
      }
      return acc;
    }, 0);

    ownerPayout += getAddOnsTotal(this.customAddOns || [], 'required', false);

    this.ownerTotalPayout = ownerPayout;

    const mockPricingMetaData = {
      ...this.charter.pricingMetaData,
      addOns: this.customAddOns || [],
    };
    const charterWithDuration = {
      ...this.charter,
      duration: this.offerDuration,
      pricingMetaData: mockPricingMetaData,
    };
    const payoutsSub = this.payoutDAO
      .createBOMockPayoutsForPricingDetails(
        this.customOriginalRates,
        charterWithDuration,
      )
      .subscribe((mockPayouts) => {
        this.customPayouts = mockPayouts.filter(
          (x) => x.name !== 'Affiliate User' && x.type !== 'addOn',
        ); // ignore affiliate payout
        let customPayoutAmount = this.customPayouts.reduce(
          (acc: number, curr: Payout) => {
            if (curr.type === 'addOn') {
              acc += parseFloat(curr.basePay);
            } else if (!['Booking Fee'].includes(curr.name)) {
              if (curr.taxPercentage > 0) {
                acc += parseFloat(curr.basePay) / (1 + curr.taxPercentage);
              } else {
                acc += parseFloat(curr.actualPay);
              }
            }
            return acc;
          },
          0,
        );
        customPayoutAmount += getAddOnsTotal(
          this.customAddOns || [],
          'required',
          false,
        );
        this.customPayoutAmount = customPayoutAmount;

        this.validatePayouts();
        this.updatingPayouts = false;
      });
    this.subs.push(payoutsSub);
  }

  calculateBookingRate(rates) {
    let markedUpTotal = 0;
    let bookingMarkUp = 0;
    let bookingRate;
    rates?.forEach((rate) => {
      if ([RateType.BookingFee].includes(rate.rateType)) {
        bookingRate = rate;
        bookingMarkUp = rate.percentageBasedRatePercent;
        return;
      }
      let rateAmount = this.calculateRatePay(rate);
      markedUpTotal +=
        rateAmount * (1 + rate.markupPercentage) * (1 + rate.taxPercentage);
    });
    const bookingFee = markedUpTotal * bookingMarkUp;
    const hourlyRate = bookingFee / this.offerDuration;
    bookingRate.hourlyRate = hourlyRate;
    bookingRate.halfDayRate = hourlyRate * 4;
    bookingRate.fullDayRate = hourlyRate * 8;

    return bookingRate;
  }

  rebuildOriginalRates() {
    const newRates = JSON.parse(JSON.stringify(this.customOriginalRates)).map(
      (rate) => {
        if (rate.rateType === RateType.Owner) {
          const ownerMarkup = this.customOriginalRates.find(
            (x) => x.rateType === RateType.Owner,
          )?.markupPercentage as number;
          const ownerPay =
            (this.offerPrice - this.sumCustomRateAmountsWithMarkup()) /
            (1 + ownerMarkup);

          rate.hourlyRate = ownerPay / this.offerDuration;
          rate.halfDayRate = rate.hourlyRate * 4;
          rate.fullDayRate = rate.hourlyRate * 8;
          this.ownerPay = ownerPay;
        } else if (rate.rateType === RateType.Cleaning) {
          rate.customRate = parseFloat(this.customRateAmounts[rate.id]);
        } else if (rate.rateType !== RateType.BookingFee) {
          const customRate = this.customRateAmounts[rate.id];
          const hourlyRate = customRate / this.offerDuration;
          rate.hourlyRate = hourlyRate;
          rate.halfDayRate = hourlyRate * 4;
          rate.fullDayRate = hourlyRate * 8;
        }
        return rate;
      },
    );

    const bookingRateIdx = newRates.findIndex(
      (x) => x.rateType === RateType.BookingFee,
    );
    const updatedBookingRate: number = this.calculateBookingRate(newRates);
    newRates[bookingRateIdx] = updatedBookingRate;
    return newRates;
  }

  closeDialog() {
    this.dialogRef.close(null);
  }

  submitCustomRates() {
    this.dialogRef.close({
      customOriginalRates: this.customOriginalRates,
      customAddOns: this.customAddOns,
    });
  }
}
